// PR-D / PR-3 — shared placement-floor utility.
//
// A completed parent/onboarding placement writes a per-strand level
// (progress.readingModuleId / progress.mathModuleId, both LEGACY module ids
// like "reading.09" / "math.11"). This module turns that into a CURRICULUM
// ORDINAL floor so callers can ask "is skill X below where this child was
// placed?" — used by the foundation route guard (PR-D) and the engine soft
// re-rank (PR-E).
//
// Why ordinal, not numeric suffix: the reading sequence interleaves
// non-numeric ids (reading.0a / reading.0b / reading.dolch.trace), so a
// naive Number(suffix) sort is wrong. readingModuleIndex/mathModuleIndex give
// the authored curriculum position, which is the only correct ranking.
//
// Why placementStatus === "complete": defaultProgress() seeds the LOWEST
// module ("reading.0a") for a brand-new learner, so a null/low module id is
// NOT a trustworthy "floor" — only a COMPLETED placement is. An un-placed
// learner therefore has no floor (no-op), exactly like the age-floor pattern.
import { readingModuleIndex } from "@/curriculum/readingModules.js";
import { mathModuleIndex } from "@/curriculum/mathModules.js";
import {
  lookupMicroSkillForLegacy,
  mapMicroSkillIdToLegacy,
} from "@/curriculum/readers/manifestReader";
import { FOUNDATION_SKILL_IDS } from "./skillMeta";
import microSkills from "@/curriculum/data/manifests/microSkills.json" with { type: "json" };
import { bandRank } from "@/engine/ageBand";

export type Strand = "reading" | "math";

interface PlacementProgressLike {
  placementStatus?: string | null;
  readingModuleId?: string | null;
  mathModuleId?: string | null;
}

export interface PlacementFloor {
  strand: Strand;
  /** legacy module id of the placed level (e.g. "reading.09"). */
  legacyId: string;
  /** microskill id AT the placed floor (e.g. "ms.read.09") — the serve target. */
  skillId: string;
  /** curriculum ordinal of the placed level within its strand. */
  ordinal: number;
  /** MA-838: set only by evidenceFloor.belowFloor — a no-entry above-seed floor used for "what is below it" only. */
  belowOnly?: boolean;
}

/** Curriculum ordinal of a LEGACY module id (-1 when unknown). */
export function legacyOrdinal(strand: Strand, legacyId: string): number {
  return strand === "reading"
    ? readingModuleIndex(legacyId)
    : mathModuleIndex(legacyId);
}

/**
 * Curriculum ordinal of a MICROSKILL id (ms.read.* / ms.math.*). Resolves the
 * skill to its legacy module(s) and returns the ordinal. A microskill that
 * expands to several legacy modules takes its EARLIEST (lowest) position — the
 * conservative rank for a below-floor test. Returns -1 when unknown.
 */
export function skillCurriculumOrdinal(strand: Strand, skillId: string): number {
  const legacies = mapMicroSkillIdToLegacy(skillId);
  let best = -1;
  for (const lg of legacies) {
    const o = legacyOrdinal(strand, lg);
    if (o >= 0 && (best < 0 || o < best)) best = o;
  }
  return best;
}

/**
 * The placement floor for a strand, or null when there's no trustworthy
 * placement (placementStatus !== "complete", or an unknown/empty module id).
 * Placement RAISES a floor; it never lowers one. No placement → null → no-op.
 */
export function placementFloor(
  strand: Strand,
  progress: PlacementProgressLike | null | undefined,
): PlacementFloor | null {
  if (!progress) return null;
  if (progress.placementStatus !== "complete") return null;
  const legacyId =
    strand === "reading" ? progress.readingModuleId : progress.mathModuleId;
  if (!legacyId) return null;
  const ordinal = legacyOrdinal(strand, legacyId);
  if (ordinal < 0) return null;
  const skillId = lookupMicroSkillForLegacy(legacyId);
  if (!skillId) return null;
  return { strand, legacyId, skillId, ordinal };
}

/**
 * Is `skillId` strictly BELOW the placement floor? Unknown skills (ordinal -1)
 * are treated as NOT below the floor — the guard never blocks content it can't
 * rank (conservative: a false "not below" just lets a skill through).
 */
export function isBelowFloor(
  skillId: string,
  floor: PlacementFloor | null,
): boolean {
  if (!floor) return false;
  const o = skillCurriculumOrdinal(floor.strand, skillId);
  if (o < 0) return false;
  return o < floor.ordinal;
}

const PREREQS_BY_ID: Record<string, readonly string[]> = Object.fromEntries(
  (microSkills as Array<{ id: string; prerequisites?: string[] }>).map((m) => [m.id, m.prerequisites ?? []]),
);

/** MA-851 — the floor skill's own prerequisite closure (everything it transitively needs; the floor
 *  skill itself is not in it). */
export function floorPrereqClosureIds(floorSkillId: string): ReadonlySet<string> {
  const out = new Set<string>();
  const stack = [...(PREREQS_BY_ID[floorSkillId] ?? [])];
  while (stack.length > 0) {
    const id = stack.pop()!;
    if (out.has(id)) continue;
    out.add(id);
    for (const p of PREREQS_BY_ID[id] ?? []) stack.push(p);
  }
  return out;
}

/**
 * MA-851 — the one "does the /play/foundation guard let this skill through below the floor"
 * exception: with the demotion valve LOWERED (the child is failing the floor skill, the
 * ranker's path down for an over-placed child), the floor skill's own unmet prerequisites
 * (its closure) are allowed, so the ranker's pick, Today, the CTA and the tap all name the
 * same easier skill. Without a lowered valve the guard is exactly MA-837's.
 */
export function guardAllowsBelowFloor(
  skillId: string,
  floor: PlacementFloor | null | undefined,
  valveLowered: boolean,
): boolean {
  return !!floor && valveLowered && floorPrereqClosureIds(floor.skillId).has(skillId);
}

/**
 * MA-851 — the skill /play/foundation's guard sends a foundation skill to: the floor
 * skill, iff `skillId` is a foundation skill strictly below the (reading) floor and not
 * allowed through (`guardAllowsBelowFloor`); null otherwise. The mission's naming rule
 * (progression.buildTodaysMission) uses this; `evidenceFloor.foundationGuardRedirect`
 * mirrors its decision (it also covers the below-only floor and does not restrict to
 * FOUNDATION_SKILL_IDS, the route only sees those), and the MA-851 test asserts they agree
 * on every foundation skill.
 */
export function foundationRedirectSkillId(
  skillId: string,
  floor: PlacementFloor | null | undefined,
  valveLowered = false,
): string | null {
  if (!floor || floor.strand !== "reading") return null;
  if (!FOUNDATION_SKILL_IDS.includes(skillId)) return null;
  if (guardAllowsBelowFloor(skillId, floor, valveLowered)) return null;
  return isBelowFloor(skillId, floor) ? floor.skillId : null;
}

/**
 * MA-301 — turn a just-completed diagnostic staircase's terminal rung
 * (already resolved to a real legacy module id by the caller, e.g.
 * `mathModuleIdForRung`/`readingModuleIdForRung`) into the module id that
 * should be WRITTEN as the strand's `mathModuleId`/`readingModuleId` going
 * forward. Same "placement RAISES a floor, never lowers one" doctrine as
 * the rest of this module: an unknown candidate, or a candidate whose
 * ordinal doesn't exceed the current module id's, changes nothing.
 *
 * `itemsAnswered` mirrors `applyPlacementBias`'s own "total must be > 0"
 * contract — an unreached/skipped diagnostic (0 items) is NO EVIDENCE, not
 * evidence of a low floor, and must not touch the stored module id.
 *
 * CALIBRATION NOTE: a reversal-stop staircase's terminal rung is the point
 * where the child's answers stopped being consistently right — it is a
 * reasonable single-session floor estimate, not a mastery claim (routing
 * only, per ADR-069's evidence-not-mastery boundary — this never touches
 * SkillState/mastery).
 */
export function raiseModuleFloorFromDiagnostic(
  strand: Strand,
  currentLegacyId: string | null | undefined,
  candidateLegacyId: string,
  itemsAnswered: number,
): string | null {
  if (!(itemsAnswered > 0)) return null;
  const candidateOrdinal = legacyOrdinal(strand, candidateLegacyId);
  if (candidateOrdinal < 0) return null;
  const currentOrdinal = currentLegacyId ? legacyOrdinal(strand, currentLegacyId) : -1;
  return candidateOrdinal > currentOrdinal ? candidateLegacyId : (currentLegacyId ?? candidateLegacyId);
}

// ── MA-1226 slice 2 (operator D-1104-31a) — curriculum POSITION for every skill.
// skillCurriculumOrdinal is -1 for a skill with no legacy module (the grade-2
// skills: ms.math.16-29, ms.read.17-22), so an ordinal compare silently skips
// them. Where either skill has no legacy ordinal, the position is the tree's
// own order: band rank (AGE_BAND_ORDER, bandRank) then the strand's
// declaration order in microSkills.json (sortStrandByAgeBand's comparator).
const TREE_POSITION: Record<Strand, Map<string, number>> = (() => {
  const out = { math: new Map<string, number>(), reading: new Map<string, number>() } as Record<Strand, Map<string, number>>;
  for (const strand of ["math", "reading"] as const) {
    (microSkills as Array<{ id: string; strand?: string; ageBand: string }>)
      .map((m, i) => ({ m, i }))
      .filter((x) => x.m.strand === strand)
      .sort((a, b) => bandRank(a.m.ageBand) - bandRank(b.m.ageBand) || a.i - b.i)
      .forEach((x, pos) => out[strand].set(x.m.id, pos));
  }
  return out;
})();

/** MA-1226 (D-1104-31a): is `skillId` at or above `floorSkillId`'s curriculum
 *  position in `strand`? The legacy ordinal when both skills have one, else the
 *  tree's order (band rank, then declaration order). Never skips a skill: a
 *  skill unknown to the manifest is false. */
export function atOrAboveFloorPosition(strand: Strand, skillId: string, floorSkillId: string): boolean {
  const a = skillCurriculumOrdinal(strand, skillId);
  const b = skillCurriculumOrdinal(strand, floorSkillId);
  if (a >= 0 && b >= 0) return a >= b;
  const pa = TREE_POSITION[strand].get(skillId);
  const pb = TREE_POSITION[strand].get(floorSkillId);
  return pa !== undefined && pb !== undefined && pa >= pb;
}
